import {
  PaymentDepositCalendarRequest,
  PaymentSettlementCalculation,
  PaymentSettlementPolicy,
} from "./paymentDepositTypes";

export const UNCONFIGURED_PAYMENT_SETTLEMENT_POLICY: PaymentSettlementPolicy = {
  profileId: "",
  feeRateBasisPoints: null,
  feeVatMode: "UNKNOWN",
  feeVatRateBasisPoints: null,
  settlementBusinessDays: null,
  roundingMode: "FLOOR",
  effectiveFrom: "",
  source: "UNCONFIGURED",
};

const isIntegerBetween = (
  value: unknown,
  minimum: number,
  maximum: number
): value is number =>
  Number.isSafeInteger(value) &&
  Number(value) >= minimum &&
  Number(value) <= maximum;

const isValidDateKey = (value = "") => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() + 1 === Number(month) &&
    date.getUTCDate() === Number(day)
  );
};

export const getPaymentSettlementPolicy = (
  request: PaymentDepositCalendarRequest,
  approvedDate = ""
): PaymentSettlementPolicy => {
  const policy = request.settlementPolicy;
  if (
    !policy ||
    policy.source === "UNCONFIGURED" ||
    !isIntegerBetween(policy.feeRateBasisPoints, 0, 10000) ||
    !isIntegerBetween(policy.settlementBusinessDays, 0, 365) ||
    !["FLOOR", "ROUND", "CEIL"].includes(policy.roundingMode) ||
    !["INCLUDED", "EXCLUDED", "UNKNOWN"].includes(policy.feeVatMode) ||
    !isValidDateKey(policy.effectiveFrom) ||
    !isValidDateKey(approvedDate) ||
    approvedDate < policy.effectiveFrom ||
    (policy.feeVatMode === "EXCLUDED" &&
      !isIntegerBetween(policy.feeVatRateBasisPoints, 0, 10000))
  ) {
    return {
      ...UNCONFIGURED_PAYMENT_SETTLEMENT_POLICY,
      profileId: String(
        policy?.profileId || request.paymentProfileId || ""
      ).slice(0, 40),
    };
  }
  return {
    ...policy,
    profileId: String(
      policy.profileId || request.paymentProfileId || ""
    ).slice(0, 40),
  };
};

const roundWon = (
  value: number,
  mode: PaymentSettlementPolicy["roundingMode"]
) => {
  if (mode === "ROUND") return Math.round(value);
  if (mode === "CEIL") return Math.ceil(value);
  return Math.floor(value);
};

const formatUtcDateKey = (date: Date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getUTCDate()).padStart(2, "0")}`;

export const addPaymentSettlementBusinessDays = (
  dateKey: string,
  businessDays: number
) => {
  if (
    !isValidDateKey(dateKey) ||
    !Number.isSafeInteger(businessDays) ||
    businessDays < 0 ||
    businessDays > 365
  ) {
    return "";
  }
  const [year, month, day] = dateKey.split("-").map(Number);
  const cursor = new Date(Date.UTC(year, month - 1, day));
  let remaining = businessDays;
  while (remaining > 0) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const weekday = cursor.getUTCDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }
  return formatUtcDateKey(cursor);
};

export const calculatePaymentSettlement = (
  request: PaymentDepositCalendarRequest,
  approvedDate = ""
): PaymentSettlementCalculation => {
  const policy = getPaymentSettlementPolicy(request, approvedDate);
  const settlementAmount = Number(
    request.remainingAmount ?? request.amount ?? 0
  );
  const manualExpectedDate = String(
    request.depositManagement?.expectedDepositDate || ""
  );
  const validAmount =
    Number.isSafeInteger(settlementAmount) && settlementAmount >= 0;
  if (!validAmount) {
    return {
      policy,
      settlementAmount: 0,
      feeAmount: null,
      feeVatAmount: null,
      totalDeductionAmount: null,
      expectedDepositAmount: null,
      expectedDepositDate: isValidDateKey(manualExpectedDate)
        ? manualExpectedDate
        : "",
      expectedDepositDateSource: isValidDateKey(manualExpectedDate)
        ? "MANUAL"
        : "UNCONFIGURED",
      holidaysApplied: false,
      errorCode: "PAYMENT_SETTLEMENT_CALCULATION_INVALID",
    };
  }

  const expectedDepositDate = isValidDateKey(manualExpectedDate)
    ? manualExpectedDate
    : policy.source !== "UNCONFIGURED" &&
      policy.settlementBusinessDays !== null
    ? addPaymentSettlementBusinessDays(
        approvedDate,
        policy.settlementBusinessDays
      )
    : "";
  const expectedDepositDateSource = isValidDateKey(manualExpectedDate)
    ? "MANUAL"
    : expectedDepositDate
    ? "POLICY"
    : "UNCONFIGURED";

  if (policy.source === "UNCONFIGURED" || policy.feeRateBasisPoints === null) {
    return {
      policy,
      settlementAmount,
      feeAmount: null,
      feeVatAmount: null,
      totalDeductionAmount: null,
      expectedDepositAmount: null,
      expectedDepositDate,
      expectedDepositDateSource,
      holidaysApplied: false,
      errorCode: "PAYMENT_SETTLEMENT_POLICY_UNCONFIGURED",
    };
  }

  const feeAmount = roundWon(
    (settlementAmount * policy.feeRateBasisPoints) / 10000,
    policy.roundingMode
  );
  const feeVatAmount =
    policy.feeVatMode === "INCLUDED"
      ? 0
      : policy.feeVatMode === "EXCLUDED" &&
        policy.feeVatRateBasisPoints !== null &&
        policy.feeVatRateBasisPoints !== undefined
      ? roundWon(
          (feeAmount * policy.feeVatRateBasisPoints) / 10000,
          policy.roundingMode
        )
      : null;
  if (feeVatAmount === null) {
    return {
      policy,
      settlementAmount,
      feeAmount,
      feeVatAmount: null,
      totalDeductionAmount: null,
      expectedDepositAmount: null,
      expectedDepositDate,
      expectedDepositDateSource,
      holidaysApplied: false,
      errorCode: "PAYMENT_SETTLEMENT_CALCULATION_INVALID",
    };
  }
  const totalDeductionAmount = feeAmount + feeVatAmount;
  const expectedDepositAmount = Math.max(
    0,
    settlementAmount - totalDeductionAmount
  );
  return {
    policy,
    settlementAmount,
    feeAmount,
    feeVatAmount,
    totalDeductionAmount,
    expectedDepositAmount,
    expectedDepositDate,
    expectedDepositDateSource,
    holidaysApplied: false,
    errorCode: "",
  };
};
